import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';

const OrdenCompraEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ordenCompra, setOrdenCompra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrdenCompra = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/ordenes_compra/${id}`);
        const ocData = response.data;

        // Initialize with empty details if null
        ocData.detalles = ocData.detalles || [];

        // Initialize impuestos array with temp_id
        if (!ocData.impuestos || ocData.impuestos.length === 0) {
          ocData.impuestos = [];
        } else {
          ocData.impuestos = ocData.impuestos.map(imp => ({ ...imp, temp_id: uuidv4() }));
        }

        setOrdenCompra({
          ...ocData,
          detalles: ocData.detalles.map(d => ({ ...d, temp_id: uuidv4() })),
        });
        setError('');
      } catch (err) {
        setError('No se pudo cargar la orden de compra para editar.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrdenCompra();
  }, [id]);

  const calculatedTotals = useMemo(() => {
    if (!ordenCompra?.detalles) return { subtotal: 0, totalImpuestos: 0, total: 0 };
    const subtotal = ordenCompra.detalles.reduce((acc, d) => acc + (parseFloat(d.importe) || 0), 0);

    // Use the monto from impuestos state directly (no auto-recalculation)
    const totalImpuestos = (ordenCompra.impuestos || []).reduce((acc, imp) => acc + (parseFloat(imp.monto) || 0), 0);
    const total = subtotal + totalImpuestos;

    return { subtotal, totalImpuestos, total };
  }, [ordenCompra?.detalles, ordenCompra?.impuestos]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrdenCompra(prev => {
      if (prev === null) return null;
      return { ...prev, [name]: value };
    });
  };

  const handleBankDataChange = (e) => {
    const { name, value } = e.target;
    setOrdenCompra(prev => {
      if (prev === null) return null;
      return {
        ...prev,
        proveedor_datos_bancarios: {
          ...prev.proveedor_datos_bancarios,
          [name]: value,
        },
      };
    });
  };

  const handleDetalleChange = (temp_id, field, value) => {
    const newDetalles = ordenCompra.detalles.map((d) => {
      if (d.temp_id === temp_id) {
        const updatedDetalle = { ...d, [field]: value };
        if (field === 'cantidad' || field === 'precio_unitario') {
            const cantidad = field === 'cantidad' ? parseFloat(value) || 0 : parseFloat(d.cantidad) || 0;
            const precio = field === 'precio_unitario' ? parseFloat(value) || 0 : parseFloat(d.precio_unitario) || 0;
            updatedDetalle.importe = cantidad * precio;
        }
        return updatedDetalle;
      }
      return d;
    });
    setOrdenCompra(prev => ({ ...prev, detalles: newDetalles }));
  };

  const addDetalle = () => {
    setOrdenCompra(prev => ({
      ...prev,
      detalles: [
        ...prev.detalles,
        { temp_id: uuidv4(), descripcion: '', cantidad: 1, unidad: 'PZA', precio_unitario: 0, importe: 0 },
      ],
    }));
  };

  const removeDetalle = (temp_id) => {
    setOrdenCompra(prev => ({
      ...prev,
      detalles: prev.detalles.filter((d) => d.temp_id !== temp_id),
    }));
  };

  const handleImpuestoChange = (temp_id, field, value) => {
    const subtotal = ordenCompra.detalles.reduce((acc, d) => acc + (parseFloat(d.importe) || 0), 0);

    const newImpuestos = ordenCompra.impuestos.map((imp) => {
      if (imp.temp_id === temp_id) {
        const updatedImpuesto = { ...imp, [field]: value };

        // Auto-calculate monto based on porcentaje and round to 10 decimals
        if (field === 'porcentaje') {
          const calculated = subtotal * (parseFloat(value) / 100);
          updatedImpuesto.monto = Math.round(calculated * 10000000000) / 10000000000;
        }

        return updatedImpuesto;
      }
      return imp;
    });
    setOrdenCompra(prev => ({ ...prev, impuestos: newImpuestos }));
  };

  const addImpuesto = () => {
    setOrdenCompra(prev => ({
      ...prev,
      impuestos: [
        ...prev.impuestos,
        { temp_id: uuidv4(), nombre: '', porcentaje: 0, monto: 0 },
      ],
    }));
  };

  const removeImpuesto = (temp_id) => {
    setOrdenCompra(prev => ({
      ...prev,
      impuestos: prev.impuestos.filter((imp) => imp.temp_id !== temp_id),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const { subtotal, totalImpuestos, total } = calculatedTotals;

    // Clean details, removing temp_id before submission
    const cleanDetalles = ordenCompra.detalles.map(({ temp_id, ...rest }) => {
        // Ensure id and orden_compra_id are not sent for new items
        if (String(rest.id).startsWith('temp_')) {
            delete rest.id;
            delete rest.orden_compra_id;
        }
        return rest;
    });

    // Clean impuestos, removing temp_id
    // Ensure all numeric values are actually numbers, not strings
    const cleanImpuestos = ordenCompra.impuestos.map(({ temp_id, ...rest }) => ({
      ...rest,
      porcentaje: parseFloat(rest.porcentaje) || 0,
      monto: parseFloat(rest.monto) || 0
    }));

    const ocDataToSubmit = {
      ...ordenCompra,
      subtotal,
      iva: totalImpuestos,  // Keep for backward compatibility, but use totalImpuestos
      total,
      impuestos: cleanImpuestos,
      detalles: cleanDetalles,
    };
    
    // Remove fields that shouldn't be sent
    delete ocDataToSubmit.empresa;
    delete ocDataToSubmit.proveedor;
    delete ocDataToSubmit.requisicion;
    delete ocDataToSubmit.adjuntos;
    delete ocDataToSubmit.notas;
    delete ocDataToSubmit.nota_preview;


    try {
      await axios.put(`/api/ordenes_compra/${id}`, ocDataToSubmit);
      navigate(`/ordenes-compra/${id}`);
    } catch (err) {
      setError('No se pudo guardar la orden de compra. Verifique los datos.');
      console.error("Submission error:", err.response?.data || err);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!ordenCompra) return <Typography>No se encontró la orden de compra.</Typography>;

  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'MXN': '$',
      'USD': '$',
      'EUR': '€'
    };
    return symbols[currencyCode] || '$';
  };

  const formatCurrency = (value, showCode = false) => {
    const symbol = getCurrencySymbol(ordenCompra.moneda || 'MXN');
    const formatted = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
    if (showCode) {
      return `${symbol}${formatted} ${ordenCompra.moneda || 'MXN'}`;
    }
    return `${symbol}${formatted}`;
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Editar Orden de Compra #{ordenCompra.id.substring(0, 6)}
        </Typography>
        <form onSubmit={handleSubmit}>
          
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Datos del Solicitante</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><TextField label="Nombre del Solicitante" name="solicitante_nombre" value={ordenCompra.solicitante_nombre || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Email del Solicitante" name="solicitante_email" value={ordenCompra.solicitante_email || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Dirección del Solicitante" name="solicitante_direccion" value={ordenCompra.solicitante_direccion || ''} onChange={handleInputChange} fullWidth multiline rows={2} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Área Solicitante" name="area" value={ordenCompra.area || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Página Web del Solicitante" name="solicitante_pagina_web" value={ordenCompra.solicitante_pagina_web || ''} onChange={handleInputChange} fullWidth /></Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Datos del Proveedor</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><TextField label="Nombre del Proveedor" name="proveedor_nombre" value={ordenCompra.proveedor_nombre || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} sm={3}><TextField label="RFC del Proveedor" name="proveedor_rfc" value={ordenCompra.proveedor_rfc || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Email del Proveedor" name="proveedor_email" value={ordenCompra.proveedor_email || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Dirección del Proveedor" name="proveedor_direccion" value={ordenCompra.proveedor_direccion || ''} onChange={handleInputChange} fullWidth multiline rows={2} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Agente de Ventas" name="proveedor_agente_ventas" value={ordenCompra.proveedor_agente_ventas || ''} onChange={handleInputChange} fullWidth /></Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Datos Bancarios y de Pago</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><TextField label="Banco" name="banco" value={ordenCompra.proveedor_datos_bancarios?.banco || ''} onChange={handleBankDataChange} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Beneficiario" name="proveedor_beneficiario" value={ordenCompra.proveedor_beneficiario || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Número de Cuenta" name="numero_cuenta" value={ordenCompra.proveedor_datos_bancarios?.numero_cuenta || ''} onChange={handleBankDataChange} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="CLABE Interbancaria" name="clabe_interbancaria" value={ordenCompra.proveedor_datos_bancarios?.clabe_interbancaria || ''} onChange={handleBankDataChange} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Referencia de Pago" name="referencia_pago" value={ordenCompra.referencia_pago || ''} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Fecha Límite de Pago" name="fecha_limite_pago" type="date" value={ordenCompra.fecha_limite_pago || ''} onChange={handleInputChange} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Condiciones y Estado</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField label="Forma de Pago" name="forma_de_pago" value={ordenCompra.forma_de_pago || ''} onChange={handleInputChange} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" name="status" value={ordenCompra.status} onChange={handleInputChange} fullWidth>
                <MenuItem value="Borrador">Borrador</MenuItem>
                <MenuItem value="Firmado Supervisión">Firmado Supervisión</MenuItem>
                <MenuItem value="Firmado Dirección General">Firmado Dirección General</MenuItem>
                <MenuItem value="Aprobada">Aprobada</MenuItem>
                <MenuItem value="Enviada">Enviada</MenuItem>
                <MenuItem value="Aceptada">Aceptada</MenuItem>
                <MenuItem value="Rechazada">Rechazada</MenuItem>
                <MenuItem value="Cancelada">Cancelada</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Observaciones" name="observaciones" value={ordenCompra.observaciones || ''} onChange={handleInputChange} fullWidth multiline rows={3} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Artículos</Typography>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              select
              label="Moneda"
              name="moneda"
              value={ordenCompra.moneda || 'MXN'}
              onChange={handleInputChange}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="MXN">MXN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </TextField>
          </Box>
          <TableContainer>
            <Table>
              <TableHead><TableRow><TableCell sx={{ width: '40%' }}>Descripción</TableCell><TableCell>Cantidad</TableCell><TableCell>Unidad</TableCell><TableCell>Precio Unitario</TableCell><TableCell>Importe</TableCell><TableCell></TableCell></TableRow></TableHead>
              <TableBody>
                {ordenCompra.detalles.map((detalle) => (
                  <TableRow key={detalle.temp_id}>
                    <TableCell><TextField value={detalle.descripcion} onChange={(e) => handleDetalleChange(detalle.temp_id, 'descripcion', e.target.value)} fullWidth variant="standard" /></TableCell>
                    <TableCell><TextField type="number" value={detalle.cantidad} onChange={(e) => handleDetalleChange(detalle.temp_id, 'cantidad', e.target.value)} variant="standard" inputProps={{ step: "0.0000000001", min: "0" }} /></TableCell>
                    <TableCell><TextField value={detalle.unidad} onChange={(e) => handleDetalleChange(detalle.temp_id, 'unidad', e.target.value)} variant="standard" /></TableCell>
                    <TableCell><TextField type="number" value={detalle.precio_unitario} onChange={(e) => handleDetalleChange(detalle.temp_id, 'precio_unitario', e.target.value)} variant="standard" inputProps={{ step: "0.0000000001", min: "0" }} /></TableCell>
                    <TableCell>{formatCurrency(detalle.importe)}</TableCell>
                    <TableCell><IconButton onClick={() => removeDetalle(detalle.temp_id)} color="error"><DeleteIcon /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button startIcon={<AddIcon />} onClick={addDetalle} sx={{ mt: 2 }}>Añadir Artículo</Button>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Impuestos</Typography>
          {ordenCompra.impuestos && ordenCompra.impuestos.map((impuesto) => (
            <Box key={impuesto.temp_id} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Nombre del Impuesto"
                value={impuesto.nombre}
                onChange={(e) => handleImpuestoChange(impuesto.temp_id, 'nombre', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Porcentaje (%)"
                type="number"
                value={impuesto.porcentaje}
                onChange={(e) => handleImpuestoChange(impuesto.temp_id, 'porcentaje', e.target.value)}
                inputProps={{ step: "0.0000000001", min: "0" }}
                sx={{ width: 150 }}
              />
              <TextField
                label="Monto"
                type="number"
                value={impuesto.monto}
                onChange={(e) => handleImpuestoChange(impuesto.temp_id, 'monto', e.target.value)}
                inputProps={{ step: "0.0000000001", min: "0" }}
                sx={{ width: 180 }}
              />
              <IconButton onClick={() => removeImpuesto(impuesto.temp_id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addImpuesto} sx={{ mt: 1 }}>Añadir Impuesto</Button>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                <Typography align="right">Subtotal: {formatCurrency(calculatedTotals.subtotal)}</Typography>
                {ordenCompra.impuestos && ordenCompra.impuestos.map((impuesto) => (
                  <Typography key={impuesto.temp_id} align="right">
                    {impuesto.nombre} ({impuesto.porcentaje}%): {formatCurrency(parseFloat(impuesto.monto) || 0)}
                  </Typography>
                ))}
                <Typography align="right" variant="h6">Total: {formatCurrency(calculatedTotals.total, true)}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(`/ordenes-compra/${id}`)} sx={{ mr: 2 }}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">Guardar Cambios</Button>
          </Box>
          {error && (<Typography color="error" sx={{ mt: 2 }}>{error}</Typography>)}
        </form>
      </Paper>
    </Container>
  );
};

export default OrdenCompraEditor;
